import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Cog, ExternalLink, TreePine } from 'lucide-react';
import type { SupplierOrg, VendorCapability } from '../../../types';
import type { VendorOrgLinkedPortalUser, VendorOrgLinkedProject } from '../../../utils/vendorOrgSummary';
import { vendorLinkedProjectHubPath } from '../../../utils/vendorOrgSummary';
import VendorDeletePanel from './VendorDeletePanel';
import { capabilityLabel, workTypeLabel } from '../../../utils/vendorHelpers';
import { rfqStatusLabel } from '../../../utils/vendorHelpers';
import { useVendorOrgEditor } from '../../../hooks/useVendorOrgEditor';
import VendorInvitePanel from './VendorInvitePanel';
import UnifiedInviteModal from '../../admin/UnifiedInviteModal';
import type { SupplierInvite } from '../../../types';

export interface VendorProfileProps {
  org: SupplierOrg;
  invites: SupplierInvite[];
  linkedProjects: VendorOrgLinkedProject[];
  linkedPortalUsers: VendorOrgLinkedPortalUser[];
  onRevokeInvite: (id: string) => void;
  onDeleteInvite: (id: string) => void;
  onDeleteVendor?: () => Promise<void>;
  deleteVendorBusy?: boolean;
}

const VendorProfile: React.FC<VendorProfileProps> = ({
  org,
  invites,
  linkedProjects,
  linkedPortalUsers,
  onRevokeInvite,
  onDeleteInvite,
  onDeleteVendor,
  deleteVendorBusy = false,
}) => {
  const editor = useVendorOrgEditor(org);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const hasCnc = editor.capabilitiesDraft.capabilities.includes('cnc');
  const createdLabel =
    org.createdAt instanceof Date ? org.createdAt.toLocaleDateString() : '';

  const toggleCap = (cap: VendorCapability) => {
    editor.setCapabilitiesDraft((prev) => {
      const next = prev.capabilities.includes(cap)
        ? prev.capabilities.filter((c) => c !== cap)
        : [...prev.capabilities, cap];
      return { ...prev, capabilities: next.length ? next : ['timber'] };
    });
  };

  return (
    <div className="vm-profile">
      <header className="vm-profile-head">
        <div className="vm-profile-head-badges">
          {(org.capabilities || ['timber']).map((c) => (
            <span key={c} className="vm-cap-badge">
              {c === 'cnc' ? <Cog size={11} aria-hidden /> : <TreePine size={11} aria-hidden />}
              {capabilityLabel(c)}
            </span>
          ))}
        </div>
        {createdLabel && <p className="vm-profile-meta">Added {createdLabel}</p>}
      </header>

      <section className="vm-ops-section">
        <h3 className="vm-ops-section-title">
          <Building2 size={14} aria-hidden /> Company details
        </h3>
        <div className="vm-ops-fields">
          <label className="vm-ops-field">
            <span className="ops-modal-label">Company name</span>
            <input
              className="ops-modal-input"
              value={editor.basicsDraft.name}
              onChange={(e) => editor.setBasicsDraft((d) => ({ ...d, name: e.target.value }))}
            />
          </label>
          <label className="vm-ops-field">
            <span className="ops-modal-label">Service region</span>
            <input
              className="ops-modal-input"
              placeholder="Optional — e.g. Northeast US"
              value={editor.basicsDraft.serviceRegion}
              onChange={(e) => editor.setBasicsDraft((d) => ({ ...d, serviceRegion: e.target.value }))}
            />
          </label>
          <label className="vm-ops-field vm-ops-field--full">
            <span className="ops-modal-label">Internal notes</span>
            <textarea
              className="ops-modal-textarea"
              rows={3}
              value={editor.basicsDraft.notes}
              onChange={(e) => editor.setBasicsDraft((d) => ({ ...d, notes: e.target.value }))}
            />
          </label>
        </div>
        <div className="vm-ops-actions">
          <button
            type="button"
            className="ops-new-btn"
            disabled={editor.savingBasics || !editor.basicsDraft.name.trim()}
            onClick={() => void editor.saveBasics()}
          >
            {editor.savingBasics ? 'Saving…' : 'Save details'}
          </button>
        </div>
      </section>

      <section className="vm-ops-section">
        <h3 className="vm-ops-section-title">Capabilities</h3>
        <p className="vm-ops-hint">What types of work this vendor can receive from LCTF.</p>
        <div className="vm-check-row">
          {(['timber', 'cnc'] as VendorCapability[]).map((cap) => (
            <label key={cap} className="vm-check">
              <input
                type="checkbox"
                checked={editor.capabilitiesDraft.capabilities.includes(cap)}
                onChange={() => toggleCap(cap)}
              />
              {capabilityLabel(cap)}
            </label>
          ))}
        </div>
        {hasCnc && (
          <div className="vm-cnc-defaults">
            <p className="vm-ops-hint">Optional CNC quoting defaults (reference for your team).</p>
            <div className="vm-cnc-defaults-grid">
              <label className="vm-ops-field">
                <span className="ops-modal-label">Flat fee ($)</span>
                <input
                  type="number"
                  className="ops-modal-input"
                  min={0}
                  value={editor.capabilitiesDraft.cncDefaultFlatFee}
                  onChange={(e) =>
                    editor.setCapabilitiesDraft((d) => ({ ...d, cncDefaultFlatFee: e.target.value }))
                  }
                />
              </label>
              <label className="vm-ops-field">
                <span className="ops-modal-label">$/bdft</span>
                <input
                  type="number"
                  className="ops-modal-input"
                  min={0}
                  step="0.01"
                  value={editor.capabilitiesDraft.cncDefaultRatePerBdft}
                  onChange={(e) =>
                    editor.setCapabilitiesDraft((d) => ({ ...d, cncDefaultRatePerBdft: e.target.value }))
                  }
                />
              </label>
              <label className="vm-ops-field">
                <span className="ops-modal-label">Lead (wks)</span>
                <input
                  type="number"
                  className="ops-modal-input"
                  min={0}
                  value={editor.capabilitiesDraft.leadTimeWeeks}
                  onChange={(e) =>
                    editor.setCapabilitiesDraft((d) => ({ ...d, leadTimeWeeks: e.target.value }))
                  }
                />
              </label>
            </div>
          </div>
        )}
        <div className="vm-ops-actions">
          <button
            type="button"
            className="ops-new-btn"
            disabled={editor.savingCapabilities || editor.capabilitiesDraft.capabilities.length === 0}
            onClick={() => void editor.saveCapabilities()}
          >
            {editor.savingCapabilities ? 'Saving…' : 'Save capabilities'}
          </button>
        </div>
      </section>

      <section className="vm-ops-section">
        <h3 className="vm-ops-section-title">RFQ notifications</h3>
        <p className="vm-ops-hint">
          Contact used for RFQ publish and quote-approval emails. First name is required for RFQ emails.
          Portal users and invites are not copied on approval emails.
        </p>
        <div className="vm-notification-fields">
          <label className="vm-ops-field">
            <span className="ops-modal-label">First name</span>
            <input
              type="text"
              className="ops-modal-input"
              placeholder="Matthew"
              value={editor.notificationFirstName}
              onChange={(e) => editor.setNotificationFirstName(e.target.value)}
            />
          </label>
          <label className="vm-ops-field">
            <span className="ops-modal-label">Notification email</span>
            <input
              type="email"
              className="ops-modal-input"
              placeholder="supplier@example.com"
              value={editor.notificationEmail}
              onChange={(e) => editor.setNotificationEmail(e.target.value)}
            />
          </label>
        </div>
        <div className="vm-ops-actions">
          <button
            type="button"
            className="ops-new-btn"
            disabled={editor.savingEmail || !editor.notificationFirstName.trim()}
            onClick={() => void editor.saveNotificationEmail()}
          >
            {editor.savingEmail ? 'Saving…' : 'Save'}
          </button>
        </div>
      </section>

      <VendorInvitePanel
        invites={invites}
        inviteName={editor.inviteName}
        inviteEmail={editor.inviteEmail}
        invitePortalRole={editor.invitePortalRole}
        inviteWorkFlags={editor.inviteWorkFlags}
        inviteSending={editor.inviteSending}
        inviteMessage={editor.inviteMessage}
        onInviteNameChange={editor.setInviteName}
        onInviteEmailChange={editor.setInviteEmail}
        onInvitePortalRoleChange={editor.setInvitePortalRole}
        onInviteWorkFlagsChange={editor.setInviteWorkFlags}
        onSendInvite={() => void editor.sendInvite()}
        onOpenUnifiedInvite={() => setShowInviteModal(true)}
        onRevoke={onRevokeInvite}
        onDelete={onDeleteInvite}
      />
      {showInviteModal && org.id && (
        <UnifiedInviteModal
          defaultRole="supplier"
          lockRole
          defaultSupplierOrgId={org.id}
          lockSupplierOrg
          onClose={() => setShowInviteModal(false)}
        />
      )}

      <section className="vm-ops-section">
        <h3 className="vm-ops-section-title">Active projects</h3>
        {linkedProjects.length === 0 ? (
          <p className="vm-empty-inline">Not assigned to any project work orders yet.</p>
        ) : (
          <ul className="vm-project-links">
            {linkedProjects.map((p) => {
              const hubPath = vendorLinkedProjectHubPath(p);
              const body = (
                <>
                  <span className="vm-project-link-title">{p.dealTitle}</span>
                  <span className="vm-cap-badge vm-cap-badge--sm">{workTypeLabel(p.workType)}</span>
                  <span className="vm-status-pill">{rfqStatusLabel(p.status)}</span>
                  {hubPath && <ExternalLink size={13} className="vm-project-link-icon" aria-hidden />}
                </>
              );
              return (
                <li key={p.rfqProjectId}>
                  {hubPath ? (
                    <Link to={hubPath} className="vm-project-link">
                      {body}
                    </Link>
                  ) : (
                    <div className="vm-project-link vm-project-link--static">{body}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {onDeleteVendor && (
        <VendorDeletePanel
          vendorName={org.name.trim() || 'this vendor'}
          linkedProjects={linkedProjects}
          linkedPortalUsers={linkedPortalUsers}
          busy={deleteVendorBusy}
          onDelete={onDeleteVendor}
        />
      )}
    </div>
  );
};

export default VendorProfile;
